import { z } from 'zod';

import { ZodAmount, ZodCurrency } from '@Common/types/Amount';
import { ZodCryptoChainNetwork } from '@Common/types/crypto/crypto_chain_network';
import { ZodDividendEvent } from '@Common/types/dividend_events';
import { ZodAnalystRating } from '@Common/types/explore/analyst_ratings';
import { InstrumentBaseType } from '@Common/types/instruments/type';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

// B2C
export const InstrumentTypeValues = ['stock-us', 'crypto', 'stock-uv-eu', 'mutual-fund'] as const;

export const ZodInstrumentType = z.enum(InstrumentTypeValues);

export const ZodSharesStatusValues = ['disabled', 'sell_only', 'suspended', 'tradable'] as const;
export const ZodSharesStatus = z.enum(ZodSharesStatusValues);

export type SharesStatus = z.TypeOf<typeof ZodSharesStatus>;

export const InstrumentType = {
  STOCK_US: 'stock-us',
  CRYPTO: 'crypto',
  STOCK_UV_EU: 'stock-uv-eu',
  MUTUAL_FUND: 'mutual-fund',
} as const;

export type InstrumentType = (typeof InstrumentType)[keyof typeof InstrumentType];

export const InstrumentAssetType = {
  STOCK: 'stock',
  CRYPTO: 'crypto',
  ETF: 'etf',
  MUTUAL_FUND: 'mutual-fund',
  COMMODITY: 'commodity',
  ELTIF: 'eltif',
  STRUCTURED_PRODUCT: 'structured-product',
} as const;
export type InstrumentAssetType = (typeof InstrumentAssetType)[keyof typeof InstrumentAssetType];

export const InstrumentAssetTypeValues = [
  'stock',
  'crypto',
  'etf',
  'mutual-fund',
  'commodity',
  'eltif',
  'structured-product',
] as const;

export const ZodInstrumentAssetType = z.enum(InstrumentAssetTypeValues);

export type AlpacaAssetExchange = 'AMEX' | 'ARCA' | 'BATS' | 'NYSE' | 'NASDAQ' | 'NYSEARCA';

export const MarketHoursExchange = {
  AMEX: 'AMEX',
  BATS: 'BATS',
  NASDAQ: 'NASDAQ',
  NYSE: 'NYSE',
  STOCK_UV_EU: 'stock-uv-eu',
  XAMS: 'XAMS',
  XFRA: 'XFRA',
  XPAR: 'XPAR',
} as const;

export type MarketHoursExchange = (typeof MarketHoursExchange)[keyof typeof MarketHoursExchange];

export type SQLPricePoint = {
  date: string;
  open?: number;
  close: number;
};

/**
 * WARNING
 *
 * If you're considering to add an additional property to this contract,
 * you MUST purge the cache for the instruments endpoints (InstrumentLight etc)
 * UNLESS you make the property `.optional()`
 *
 * Otherwise, you risk breaking the instrument endpoints completely due to stale data.
 */
export const ZodInstrumentBaseItem = z.object({
  instrument_id: z.number(),
  name: ZodTrimmedString,
  ticker: ZodTrimmedString,
  image_url: ZodTrimmedString.nullable(),
  color: ZodTrimmedString.optional().nullable(),
  dark_color: ZodTrimmedString.optional().nullable(),
  chart_currency: ZodCurrency,
  full_ticker: ZodTrimmedString,
  market_cap: z.number().nullable(),
  dividend_yield: z.number().nullable(),
  type: ZodInstrumentType,
  asset_type: ZodInstrumentAssetType,
  shares_status: ZodSharesStatus,
  is_tradable_custom_investment_plans: z.boolean(),
  isin: ZodTrimmedString.nullable(),
  instrument_issuer: ZodTrimmedString.nullable().default(null),
});
export type InstrumentBaseItem = z.infer<typeof ZodInstrumentBaseItem>;

export const ZodInstrumentTag = z.enum([
  'adr',
  'fractionable',
  'accumulating',
  'distributing',
  'physical',
  'sampling',
  'synthetical',
  'commodity',
  'etf',
  'stock',
  'crypto',
]);
export type InstrumentTag = z.infer<typeof ZodInstrumentTag>;

export const ZodInstrumentMutualFundAssetClass = z.enum([
  'diversified',
  'equity',
  'bond',
  'money_market',
  'real_estate',
]);
export type InstrumentMutualFundAssetClass = z.TypeOf<typeof ZodInstrumentMutualFundAssetClass>;

export const ZodInstrumentManagementType = z.enum(['active', 'passive']);
export type InstrumentManagementType = z.TypeOf<typeof ZodInstrumentManagementType>;

export const MutualFundValuationFrequencyValues = [
  'daily',
  'weekly',
  'biweekly',
  'semi_monthly',
  'monthly',
  'bi_monthly',
  'quarterly',
  'semi_annual',
  'annual',
  'ongoing',
  'unknown',
] as const;

export const ZodMutualFundValuationFrequency = z.enum(MutualFundValuationFrequencyValues);

export type MutualFundValuationFrequency = z.TypeOf<typeof ZodMutualFundValuationFrequency>;

export const ZodB2bInstrumentNavFrequency = z
  .enum(MutualFundValuationFrequencyValues)
  .exclude(['ongoing', 'unknown']);

export type B2bInstrumentNavFrequency = z.TypeOf<typeof ZodB2bInstrumentNavFrequency>;

/**
 * WARNING
 *
 * If you're considering to add an additional property to this contract,
 * you MUST purge the cache for the instruments endpoints (InstrumentLight etc)
 * UNLESS you make the property `.optional()`
 *
 * Otherwise, you risk breaking the instrument endpoints completely due to stale data.
 */
export const ZodGetPlainInstrumentResponse = z.object({
  ...ZodInstrumentBaseItem.shape,
  previous_close: z.number().optional(),
  chart_currency: ZodCurrency,
  can_buy: z.boolean(),
  is_provider_healthy: z.boolean(),
  tags: z.array(ZodInstrumentTag),
  kid_document_url: ZodTrimmedString.nullable(),
  mutual_fund_asset_class: ZodInstrumentMutualFundAssetClass.optional(),
  risk: z.number().int().optional(),
  management_type: ZodInstrumentManagementType.optional(),
  support_code: ZodTrimmedString.nullable().optional(),
  default_network: ZodTrimmedString.nullish(),
  default_chain_network: ZodCryptoChainNetwork.nullish(),
});

export type GetPlainInstrumentResponse = z.infer<typeof ZodGetPlainInstrumentResponse>;

// GET instruments/${id}/crypto_metrics

export type LinkType =
  | 'homepage'
  | 'blockchain_site'
  | 'official_forum_url'
  | 'chat_url'
  | 'announcement_url';

export type InstrumentLinks = Array<InstrumentLinkOne>;

export type InstrumentLinkOne = {
  type: LinkType;
  link: string;
};

export type InstrumentRating = 'strong_sell' | 'sell' | 'hold' | 'buy' | 'strong_buy';

// GET profile/portfolio

export const ZodInstrumentInvestments = z.object({
  instrument_id: z.number(),
  ticker: ZodTrimmedString,
  name: ZodTrimmedString.optional(),
  market_price: z.number().nullable(),
  can_sell: z.boolean(),
  shares_owned: z.number(),
  total_invested: ZodAmount.optional(),
  total_gain: ZodAmount.optional(),
  total_value: ZodAmount.optional(),
  pl_delta_percent: ZodTrimmedString.optional(),
  pl_allocation_percent: z.number().optional(),
  avg_price: ZodAmount.optional(),
  gain_loss: ZodAmount.optional(),
  fx_rate_impact: ZodAmount.optional(),
  portfolio_percent: ZodTrimmedString.optional(),
  avg_holding_time: z.number().optional(),
  nb_trades: z.number().optional(),
  return_1d: z.number(),
});

export type InstrumentInvestments = z.infer<typeof ZodInstrumentInvestments>;

export type InstrumentAggBySymbol = {
  symbol: string;
  type: InstrumentBaseType;
  ids: number[];
};

export const ZodGetInstrumentDividendEventsResponse = z.array(ZodDividendEvent);

export type GetInstrumentDividendEventsResponse = z.infer<
  typeof ZodGetInstrumentDividendEventsResponse
>;

const ZodGetInstrumentMetricsResponse = z.object({
  about: ZodTrimmedString.nullable(),
  ceo: ZodTrimmedString.nullable(),
  nb_employees: z.number().nullable(),
  sectors: z.array(ZodTrimmedString),
  company_address: ZodTrimmedString.nullable(),
  company_url: ZodTrimmedString.nullable(),
  market_cap: ZodAmount.nullable(),
  price_to_earnings: z.number().nullable(),
  basic_eps: z.number().nullable(),
  dividend_yield: z.number().nullable(),
  five_year_beta: z.number().nullable(),
  daily_volume: z.number().nullable(),
  analysts_number: z.number().nullable(),
  rating: ZodAnalystRating.nullable(),
  rating_updated_at: ZodTrimmedString.nullable(),
  exchange: ZodTrimmedString,
});

export type GetInstrumentMetricsResponse = z.TypeOf<typeof ZodGetInstrumentMetricsResponse>;

const ZodCoinbaseCryptoLink = z.object({
  type: z.enum(['website', 'whitepaper']),
  link: ZodTrimmedString,
});

export type CoinbaseCryptoLink = z.TypeOf<typeof ZodCoinbaseCryptoLink>;

const ZodGetCryptoInstrumentMetricsResponse = z.object({
  market_cap: ZodAmount.nullable(),
  market_cap_rank: z.number().int().nullable(),
  ath: ZodAmount.nullable(),
  market_cap_change_24h: ZodAmount.nullable(),
  creator: ZodTrimmedString.nullable(),
  genesis_year: z.number().int().nullable(),
  chain: ZodTrimmedString.nullable(),
  ecosystem: ZodTrimmedString.nullable(),
  description: ZodTrimmedString.nullable(),
  links: z.array(ZodCoinbaseCryptoLink),
});

export type GetCryptoInstrumentMetricsResponse = z.TypeOf<
  typeof ZodGetCryptoInstrumentMetricsResponse
>;

export const ZodInstrumentPositionMarketPriceRequest = z.object({
  instrumentIds: z.array(z.number()).min(1),
});

export type GetInstrumentsPositionMarketPriceRequest = z.TypeOf<
  typeof ZodInstrumentPositionMarketPriceRequest
>;
export const ZodPositionTimescale = z.enum(['1d', '1w', '1m', '3m', '6m', '1y', '3y', '5y', 'all']);
export type PositionTimescale = z.TypeOf<typeof ZodPositionTimescale>;
export const ZodInstrumentPosition = z.object({
  timescale: ZodPositionTimescale,
  position: z.number().nullable(),
});

export const ZodInstrumentsPositionMarketPrice = z.object({
  instrument_id: z.number(),
  periods: z.array(ZodInstrumentPosition),
  market_price: ZodAmount.nullable(),
  previous_close: z.number().nullable(),
});

export const ZodGetInstrumentsPositionMarketPriceResponse = z.object({
  prices: z.array(ZodInstrumentsPositionMarketPrice),
});

export type PeriodPosition = z.TypeOf<typeof ZodInstrumentPosition>;

export type InstrumentsPositionMarketPrice = z.TypeOf<typeof ZodInstrumentsPositionMarketPrice>;

export type GetInstrumentsPositionMarketPriceResponse = z.TypeOf<
  typeof ZodGetInstrumentsPositionMarketPriceResponse
>;

export const getInstrumentIdCryptoMetricsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetCryptoInstrumentMetricsResponse,
});

export type GetInstrumentIdCryptoMetricsEndpoint = GetEndpointType<
  typeof getInstrumentIdCryptoMetricsEndpoint
>;

export const postInstrumentsPositionMarketPriceEndpoint = endpoint({
  method: 'POST',
  body: ZodInstrumentPositionMarketPriceRequest,
  responseBody: ZodGetInstrumentsPositionMarketPriceResponse,
});

export type PostInstrumentsPositionMarketPriceEndpoint = GetEndpointType<
  typeof postInstrumentsPositionMarketPriceEndpoint
>;

export const ZodGetPlainInstrumentPathParam = createZodParamsValidator(
  z.object({
    id: ZodId.or(ZodTrimmedString),
  }),
);

export const getInstrumentsInstrumentIdPlainEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetPlainInstrumentPathParam,
  responseBody: ZodGetPlainInstrumentResponse,
});

export type GetInstrumentsInstrumentIdPlainEndpoint = GetEndpointType<
  typeof getInstrumentsInstrumentIdPlainEndpoint
>;

export const getInstrumentsInstrumentIdLightEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodInstrumentBaseItem,
});

export type GetInstrumentsInstrumentIdLightEndpoint = GetEndpointType<
  typeof getInstrumentsInstrumentIdLightEndpoint
>;

export const getInstrumentsInstrumentIdMetricsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetInstrumentMetricsResponse,
});

export type GetInstrumentsInstrumentIdMetricsEndpoint = GetEndpointType<
  typeof getInstrumentsInstrumentIdMetricsEndpoint
>;

// B2B
export const B2bInstrumentTypeValues = ['mutual-fund', 'insurance-product', 'ccb'] as const;

export const ZodB2bInstrumentType = z.enum(B2bInstrumentTypeValues);

export const B2bInstrumentType = {
  MUTUAL_FUND: 'mutual-fund',
  INSURANCE_PRODUCT: 'insurance-product',
  CCB: 'ccb',
} as const;

export type B2bInstrumentType = (typeof B2bInstrumentType)[keyof typeof B2bInstrumentType];

export const B2bInstrumentAssetType = {
  MUTUAL_FUND: 'mutual-fund',
  EURO_FUND: 'euro-fund',
  CCB: 'ccb',
} as const;
export type B2bInstrumentAssetType =
  (typeof B2bInstrumentAssetType)[keyof typeof B2bInstrumentAssetType];

export const B2bInstrumentAssetTypeValues = ['mutual-fund', 'euro-fund', 'ccb'] as const;

export const ZodB2bInstrumentAssetType = z.enum(B2bInstrumentAssetTypeValues);
